import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserRole } from '@/types/user';
import type { ProfilesView, SessionView } from '@/types/session';

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';
interface Preferences {
  language: string;
  timezone: string;
}
interface AuthState {
  session: SessionView | null;
  profiles: ProfilesView | null;
  user: User | null;
  /** Obsolete domain consumers still read this; cookie sessions never expose a bearer token. */
  token: null;
  isAuthenticated: boolean;
  status: SessionStatus;
  sessionError: string | null;
  username: string | null;
  selectedView: { principalId: string; role: UserRole } | null;
  welcomeSeen: boolean;
  preferences: Preferences;
  setSession: (
    session: SessionView,
    role?: UserRole,
    username?: string
  ) => void;
  setProfiles: (profiles: ProfilesView) => void;
  setSessionError: (message: string) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setPreferences: (preferences: Partial<Preferences>) => void;
  setWelcomeSeen: (seen: boolean) => void;
}

const defaults = { language: 'es', timezone: 'mad' };
function persistedPreferences(value: unknown) {
  const old = value as Partial<AuthState> | null;
  return {
    welcomeSeen: old?.welcomeSeen === true,
    preferences: {
      language:
        typeof old?.preferences?.language === 'string'
          ? old.preferences.language
          : defaults.language,
      timezone:
        typeof old?.preferences?.timezone === 'string'
          ? old.preferences.timezone
          : defaults.timezone,
    },
    selectedView:
      old?.selectedView &&
      typeof old.selectedView.principalId === 'string' &&
      ['client', 'developer'].includes(old.selectedView.role)
        ? old.selectedView
        : null,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      profiles: null,
      user: null,
      token: null,
      isAuthenticated: false,
      status: 'loading',
      sessionError: null,
      username: null,
      selectedView: null,
      welcomeSeen: false,
      preferences: defaults,
      setSession: (session, role, username) => {
        const previous = get();
        const sameUser = previous.session?.principalId === session.principalId;
        const selectedRole =
          role ??
          (previous.selectedView?.principalId === session.principalId
            ? previous.selectedView.role
            : undefined);
        set({
          session,
          token: null,
          isAuthenticated: true,
          status: 'authenticated',
          sessionError: null,
          profiles: sameUser ? previous.profiles : null,
          username: username ?? (sameUser ? previous.username : null),
          selectedView: selectedRole
            ? { principalId: session.principalId, role: selectedRole }
            : null,
          user: {
            name: session.displayName,
            principalId: session.principalId,
            accountId: session.accountId,
            role: selectedRole,
          },
        });
      },
      setProfiles: (profiles) => {
        if (profiles.principalId === get().session?.principalId)
          set({ profiles });
      },
      setSessionError: (message) =>
        set({ status: 'error', sessionError: message }),
      logout: () =>
        set({
          session: null,
          profiles: null,
          user: null,
          token: null,
          username: null,
          isAuthenticated: false,
          status: 'unauthenticated',
          sessionError: null,
        }),
      setUser: (user) => set({ user }),
      setPreferences: (preferences) =>
        set((state) => ({
          preferences: { ...state.preferences, ...preferences },
        })),
      setWelcomeSeen: (welcomeSeen) => set({ welcomeSeen }),
    }),
    {
      name: 'abako-auth-storage',
      version: 1,
      migrate: (old) => persistedPreferences(old),
      // Even a tampered or old persisted record cannot establish authentication.
      merge: (persisted, current) => ({
        ...current,
        ...persistedPreferences(persisted),
      }),
      partialize: (state) => ({
        welcomeSeen: state.welcomeSeen,
        preferences: state.preferences,
        selectedView: state.selectedView,
      }),
    }
  )
);
